<template>
  <div class="attendance-container">
    <div class="attendance-wrapper">
      <!-- Header Section -->
      <div class="header-section">
        <div>
          <img 
            src="/img/logo-bib-color.png" 
            alt="Logo" 
            class="logo-img"
            @error="handleLogoError">
        </div>
        <h1 class="page-title">Training Attendance</h1>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="mt-3 text-muted">Loading session details...</p>
      </div>

      <!-- Main Content -->
      <div v-else-if="sessionDetails" class="content-section">
        <!-- Training Title Card -->
        <div class="info-card full-width">
          <label class="card-label">Training</label>
          <div class="card-value">{{ sessionDetails.training_title || '-' }}</div>
        </div>

        <!-- Details Row -->
        <div class="details-row">
          <div class="info-card">
            <label class="card-label">Batch</label>
            <div class="card-value">{{ formatBatchLabel(sessionDetails) }}</div>
          </div>
          <div class="info-card">
            <label class="card-label">Date</label>
            <div class="card-value">{{ formatDate(sessionDetails.date) || '-' }}</div>
          </div>
          <div class="info-card">
            <label class="card-label">Day</label>
            <div class="card-value">Day {{ sessionDetails.day || '1' }}</div>
          </div>
        </div>

        <!-- Form Section -->
        <div class="form-section">
            <!-- Company Field -->
          <div class="form-group">
            <label class="form-label">Company</label>
            <v-select
              v-model="selectedCompany"
              :options="companyOptions"
              :reduce="option => option.value"
              label="label"
              placeholder="Select Company"
              :filterable="true"
              :clearable="true"
              @input="onCompanyChange">
            </v-select>
          </div>

          <!-- Attendance Name Field -->
          <div class="form-group">
            <label class="form-label">Attendance Name</label>
            <v-select
              v-model="selectedParticipant"
              :options="participantOptions"
              :reduce="option => option.value"
              label="label"
              placeholder="Select Participant"
              :filterable="true"
              :clearable="true"
              :disabled="!selectedCompany">
            </v-select>
          </div>

          <!-- Submit Button -->
          <button 
            type="button" 
            class="submit-button"
            :disabled="!canSubmit || submitLoading"
            @click="handleSubmit">
            <span v-if="submitLoading">
              <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Mengirim...
            </span>
            <span v-else>
              Submit Attendance
            </span>
          </button>
        </div>
      </div>

      <!-- Error State -->
      <div v-else class="error-state">
        <i class="fe fe-alert-circle error-icon"></i>
        <p class="error-message">{{ error || 'Session not found' }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { formatBatchLabel } from '@/utils/batchIdentity';

export default {
  name: 'Attendance',
  data() {
    return {
      token: null,
      selectedParticipant: null,
      selectedCompany: null,
      participantSearchQuery: '',
      companySearchQuery: ''
    };
  },
  computed: {
    ...mapGetters('attendance', [
      'sessionDetails',
      'participants',
      'companies',
      'loading',
      'error',
      'submitLoading'
    ]),
    
    participantOptions() {
      return this.participants.map(p => ({
        label: `${p.name} (${p.nik})`,
        value: p
      }));
    },
    
    companyOptions() {
      return this.companies.map(c => ({
        label: c,
        value: c
      }));
    },
    
    canSubmit() {
      return this.selectedParticipant && this.selectedCompany;
    }
  },
  methods: {
    formatBatchLabel,
    ...mapActions('attendance', [
      'getAttendanceSession',
      'getParticipants',
      'getCompanies',
      'submitAttendance'
    ]),
    
    async fetchSessionDetails() {
      try {
        await this.getAttendanceSession(this.token);
        // Fetch companies only
        await this.getCompanies(this.token);
      } catch (error) {
        console.error('Error fetching session details:', error);
      }
    },
    
    async onCompanyChange(company) {
      // Reset participant selection when company changes
      this.selectedParticipant = null;
      this.$store.commit('attendance/SET_PARTICIPANTS', []);
      
      // Fetch participants for selected company
      if (company) {
        try {
          await this.getParticipants({ 
            token: this.token, 
            company: company 
          });
        } catch (error) {
          console.error('Error fetching participants:', error);
        }
      }
    },
    
    async handleSubmit() {
      if (!this.canSubmit) return;
      
      try {
        const data = {
          token: this.token,
          nik: this.selectedParticipant?.nik
        };
        
        await this.submitAttendance(data);
        
        // Show success message
        this.$swal({
          title: 'Success',
          text: 'Absensi berhasil dikirim',
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#A52A2A'
        }).then(() => {
          // Reset form
          this.selectedParticipant = null;
          this.selectedCompany = null;
        });
      } catch (error) {
        this.$swal({
          title: 'Error',
          text: error.message || 'Absensi belum berhasil dikirim',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#A52A2A'
        });
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return '';
      
      const date = new Date(dateString);
      const day = date.getDate();
      const month = date.toLocaleDateString('en-US', { month: 'long' });
      const year = date.getFullYear();
      
      return `${day} ${month} ${year}`;
    },
    
    handleLogoError(event) {
      event.target.style.display = 'none';
    }
  },
  mounted() {
    // Get token from query params
    this.token = this.$route.query.token;
    
    if (this.token) {
      this.fetchSessionDetails();
    } else {
      // Show error if no token provided
      this.$store.commit('attendance/SET_ERROR', 'No attendance token provided');
    }
  }
};
</script>

<style scoped>
/* Container & Layout */
.attendance-container {
  min-height: 100vh;
  background-color: #F8F9FA;
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  font-family: 'Inter', 'Montserrat', 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.attendance-wrapper {
  width: 100%;
  max-width: 450px;
  margin: 0 auto;
}

/* Header Section */
.header-section {
  text-align: center;
  margin-bottom: 32px;
}

.logo-img {
  margin-bottom: 20px;  
  max-width: 360px;
  max-height: 60px;
  object-fit: contain;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
  letter-spacing: -0.5px;
}

/* Loading & Error States */
.loading-state,
.error-state {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
}

.error-icon {
  font-size: 48px;
  color: #e53e3e;
  margin-bottom: 16px;
}

.error-message {
  color: #4a5568;
  font-size: 16px;
  margin: 0;
}

/* Content Section */
.content-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Info Cards */
.info-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
}

.info-card.full-width {
  width: 100%;
}

.card-label {
  font-size: 12px;
  font-weight: 500;
  color: #718096;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 8px 0;
  display: block;
}

.card-value {
  font-size: 16px;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
}

.card-detail {
  font-size: 12px;
  font-weight: 400;
  color: #718096;
  margin-top: 4px;
}

/* Details Row */
.details-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.details-row .info-card {
  text-align: center;
}

.details-row .card-value {
  font-size: 14px;
}

/* Form Section */
.form-section {
  margin-top: 8px;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #4a5568;
  margin-bottom: 8px;
}

/* Custom Select Styles */
.custom-select {
  font-family: inherit;
}

.custom-select >>> .vs__dropdown-toggle {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 8px 12px;
  min-height: 48px;
  transition: all 0.2s ease;
}

.custom-select >>> .vs__dropdown-toggle:hover {
  border-color: #cbd5e0;
}

.custom-select.vs--open >>> .vs__dropdown-toggle {
  border-color: #3182ce;
  box-shadow: 0 0 0 3px rgba(49, 130, 206, 0.1);
}

.custom-select >>> .vs__selected {
  color: #1a202c;
  font-weight: 500;
}

.custom-select >>> .vs__search {
  font-size: 15px;
  padding: 4px 0;
}

.custom-select >>> .vs__search::placeholder {
  color: #a0aec0;
}

.custom-select >>> .vs__actions {
  padding: 4px 6px;
}

.custom-select >>> .vs__open-indicator {
  fill: #718096;
}

.custom-select >>> .vs__dropdown-menu {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1);
  margin-top: 4px;
  padding: 4px;
}

.custom-select >>> .vs__dropdown-option {
  border-radius: 8px;
  padding: 10px 12px;
  color: #4a5568;
  transition: all 0.15s ease;
}

.custom-select >>> .vs__dropdown-option--highlight {
  background: #edf2f7;
  color: #1a202c;
}

.select-option {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.option-name {
  font-weight: 500;
  color: #1a202c;
}

.option-detail {
  font-size: 12px;
  color: #718096;
}

/* Submit Button */
.submit-button {
  width: 100%;
  background: #A52A2A;
  color: white;
  border: none;
  border-radius: 12px;
  padding: 16px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 6px -1px rgba(165, 42, 42, 0.3);
  margin-top: 8px;
}

.submit-button:hover:not(:disabled) {
  background: #8B2323;
  transform: translateY(-2px);
  box-shadow: 0 6px 12px -2px rgba(165, 42, 42, 0.4);
}

.submit-button:active:not(:disabled) {
  transform: translateY(0);
}

.submit-button:disabled {
  background: #cbd5e0;
  cursor: not-allowed;
  box-shadow: none;
}

.me-2 {
  margin-right: 0.5rem;
}

/* Responsive */
@media (max-width: 480px) {
  .attendance-container {
    padding: 16px;
  }
  
  .page-title {
    font-size: 24px;
  }
  
  .details-row {
    gap: 8px;
  }
  
  .details-row .card-value {
    font-size: 13px;
  }
  
  .info-card {
    padding: 14px;
  }
}

@media (min-width: 481px) {
  .attendance-wrapper {
    margin-top: 40px;
  }
}
</style>
